/**
 * featureArea.ts
 * Static configuration for live-trading cockpit feature areas.
 *
 * Provides the canonical registry of feature areas used by the pane shell,
 * features dropdown, and slot assignment logic.
 *
 * @see types/shared/FeatureArea.ts — type definitions
 * @see components/TradingHub/LiveTrading/LiveTradingPhase.tsx — cockpit shell
 */

import { ChartCandlestick, StickyNote, TrendingUp, Calendar, Target, Coffee } from 'lucide-react';

import type { FeatureAreaConfig } from '@/types/shared/FeatureArea';

// ===== FEATURE AREAS REGISTRY =====

/**
 * Static configuration for cockpit feature areas.
 * Entry points (icon, label, color) are owned by the cockpit shell.
 * Drawer/pane content is owned by the feature WU (WU2–WU6).
 */
export const FEATURE_AREAS: readonly FeatureAreaConfig[] = [
  { id: 'trades', label: 'Trades', description: 'Trade entry and active positions', emptyHint: 'Trade entry and position management will be available in a future update.', Icon: ChartCandlestick, iconColorClass: 'text-primary' },
  { id: 'notes', label: 'Notes', description: 'Quick categorized notes', emptyHint: 'Quick categorized notes for capturing observations during trading will be available in a future update.', Icon: StickyNote, iconColorClass: 'text-warning-600' },
  { id: 'market-state', label: 'Market State', description: 'Market context and key levels', emptyHint: 'Market context snapshots and key level tracking will be available in a future update.', Icon: TrendingUp, iconColorClass: 'text-success-600' },
  { id: 'economic-events', label: 'Events', description: 'Economic calendar and alerts', emptyHint: 'Economic calendar integration and event alerts will be available in a future update.', Icon: Calendar, iconColorClass: 'text-secondary-600' },
  { id: 'objectives', label: 'Objectives', description: 'Session goals and targets', emptyHint: 'Session objective tracking and goal display will be available in a future update.', Icon: Target, iconColorClass: 'text-danger-600' },
  { id: 'break-reset', label: 'Break', description: 'Mental reset protocols', emptyHint: 'Break protocols and mental reset routines will be available in a future update.', Icon: Coffee, iconColorClass: 'text-default-600' },
] as const;
